<template lang="pug">
.v-field-file.field-file(:class="field.className")
	template(v-if="!field.preview")
		transition(name="fade" )
			transition-group.field-file__list.flc(tag="div" name="content-fade" v-if="localFiles.length")
				.field-file__item(v-for="file in localFiles" :key="file.key")
					.field-file__item-start {{file.name}}
					.field-file__item-middle {{formatBytes(file.size)}}
					.field-file__item-end
						button.field-file__item-remove.link(v-if="field.remove" type="button" @click="remove(file)") {{ field.remove }}

		.field-file__main
			.field-file__cell-input
				.field-file__input-block
					label.field-label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
						| {{field.label}}
						span.field-text__star(v-if="field.required") *
					input.field-file__input(
						:id="uniqueId"
						type="file"
						multiple="field.multiple"
						@change="fileInput"
						:key="rerenderKey"
						ref="input")
					button.field-file__btn.btn(v-if="field.add"
						type="button"
						:disabled="computedDisable"
						@click="$refs['input'].click()"
						@keydown.enter="$refs['input'].click()")
						| {{ field.add }}

			.field-file-preview__errors.flc(v-if="localErrors?.length")
				.error-list.flc
					.error-list__item.error-message.flc(v-for="(error, index) in localErrors" :key="index") {{ error }}

	template(v-if="field.preview")
		.field-file__main
			label.field-label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
				| {{field.label}}
				span.field-text__star(v-if="field.required") *
			input.field-file-preview__input(type="file" @change="fileInput" multiple="field.multiple" :key="rerenderKey" ref="input" tabindex="-1")
			.field-file-preview__note(v-if="field.note" ) {{ field.note }}
			.field-file-preview__layout
				template(v-if="localFiles && localFiles.length" )
					button.field-file-preview__visual(
							v-for="(file, index) in localFiles"
							:key="file.key"
							type="button"
							tabindex="-1")
						img.field-file-preview__image(v-if="file?.path && field.typeContent === 'image'"
							:src="file.path" alt="")
						video.field-file-preview__image(v-else-if="file?.path && field.typeContent === 'video'"
							:src="file.path" alt="")
						button.field-file-preview__action-link--remove(
								@click="remove(file)")
							v-svg-icon.field-file-preview__action-icon(icon="delete")
						input.field-file-preview__action-link--main(
								v-if="field.typeContent === 'image'"
								:id="`action-main-${index}`"
								:checked="file.key === mainImageKey"
								ref="checkbox"
								type="checkbox"
								@change="checkMainImage(file.key)")
						label.field-file-preview__action-link-box(
								v-if="field.typeContent === 'image'"
								ref="checkboxView"
								:for="`action-main-${index}`")
							.field-file-preview__action-link-visual
							.field-file-preview__action-link-text(v-if="field.variables" ) {{ field.variables?.main || '' }}
				.field-file-preview__main
					.field-file-preview__actions
						.field-file-preview__action-link(
								@click="$refs['input'].click()"
								@keydown.enter="$refs['input'].click()"
								:class="{'disabled': computedDisable}")
							.field-file-preview__action-button
								v-svg-icon.field-file-preview__action-icon(icon="plus-icon")

			.field-file-preview__errors.flc(v-if="localErrors?.length")
				.error-list.flc
					.error-list__item.error-message.flc(v-for="(error, index) in localErrors" :key="index") {{ error }}
</template>

<script>
import uniqueId from '../scripts/helpers/uniqueId.js';
import formattingMixin from '../components/mixins/v-mixin-formatting.vue';

export default {
	mixins: [
		formattingMixin
	],

	props: {
		note: {
			type: String,
			default: null
		},

		field: {
			type: Object,
			default: null
		},

		modelValue: {
			type: [Object, String, Number, Boolean],
			default: null
		},
	},
	
	emits: [
		'fileinput',
		'fileremove',
		'update:model-value'
	],

	data() {
		return {
			localFiles: [],
			localErrors: [],
			rerenderKey: 0,
			mainImageKey: null,
			uniqueId: uniqueId('field-')
		};
	},

	watch: {
		value() {
			this.initLocalValueFromValue();
		}
	},

	created() {
		this.initLocalValueFromValue(true);
	},

	mounted() {
		this.updateMainImage(this.$refs.checkbox, this.$refs.checkboxView)
	},

	updated() {
		this.updateMainImage(this.$refs.checkbox, this.$refs.checkboxView)
	},

	methods: {
		getKey() {
			return uniqueId();
		},

		updateMainImage(checkbox, checkboxView) {
			if(checkbox) {
				checkbox.forEach((checkbox, index) => {
					if (!checkbox.checked) {
						checkboxView[index].classList.add('hidden')
					}
				});

				if(checkbox.every(checkbox => !checkbox.checked)) {
					checkbox.forEach((checkbox, index) => {
						if (!checkbox.checked ) {
							checkboxView[index].classList.remove('hidden')
						}
					});
				}
			}
		},

		checkMainImage(key) {
			this.$refs.checkbox.forEach((checkbox, index) => {
				this.mainImageKey = key;
				if (!checkbox.checked) {
					this.$refs.checkboxView[index].classList.add('hidden')
				}
			});

			if(this.$refs.checkbox.every(checkbox => !checkbox.checked)) {
				this.$refs.checkbox.forEach((checkbox, index) => {
					this.mainImageKey = null;
					if (!checkbox.checked ) {
						this.$refs.checkboxView[index].classList.remove('hidden')
					}
				});
			}

			this.field.main = this.mainImageKey
		},

		fileInput(e) {
			let newKey = this.getKey();

			if (!this.field.multiple) {
				this.localFiles.forEach(this.remove);
			}

			// always 1 file per event! (no multiple <input>)
			if (e.target.files?.length) {
				const files = Array.from(e.target.files);

				if(this.field.multipleLimit && this.field.multiple) {
					const countLocalFiles = this.localFiles?.length || 0;
					files.splice(this.field.multipleLimit - countLocalFiles, files.length);
				}

				this.localErrors = [];

				if (this.field.maxSize && e.target.files[0].size > this.field.maxSize) {
					this.localErrors.push('Файл превышает максимальный размер');
				} else if (this.field.typeContent && e.target.files[0].type.indexOf(this.field.typeContent) < 0) {
					this.localErrors.push('Недопустимое расширение файла');
				}

				if (this.localErrors.length) {
					return;
				}

				// FileReader support
				for (let i = 0; i < files.length; i++) {
					if (FileReader && this.field.typeContent) {
						const fr = new FileReader();

						fr.readAsDataURL(files[i]);
						let newKey = this.getKey();
						files[i].key = newKey;

						fr.onload = () => {
							const imgPath = fr.result;

							this.localFiles.push({
								name: files[i].name,
								size: files[i].size,
								type: files[i].type,
								path: imgPath,
								key: newKey
							});
						};
					} else {
						this.localFiles.push({
							name: e.target.files[i].name,
							size: e.target.files[i].size,
							type: e.target.files[i].type,
							key: newKey
						});
					}
				}
				e.key = newKey;

				if(this.field.multiple) {
					this.$emit('fileinput', {
						key: e.key,
						target: {
							files: files
						}
					});
				} else {
					this.$emit('fileinput', e);
				}

				// this.localValue = this.localFiles
				// this.$emit('update:model-value', this.localValue);
			}

			this.rerenderKey = this.getKey();
		},

		remove(file) {
			this.localErrors = [];
			this.localFiles.splice(this.localFiles.indexOf(file), 1);
			this.$emit('fileremove', file);
			// this.localValue = this.localFiles
			// this.$emit('update:model-value', this.localValue);
		},

		updateLocalValue() {
			this.localValue = this.modelValue;
		},

		initLocalValueFromValue(initial) {
			if (this.field.files && initial) {
				this.localFiles = [];

				this.field.files.forEach((file) => {
					let newKey = this.getKey();

					if(this.field?.main) {
						this.mainImageKey = this.field.main
					}

					this.localFiles.push({
						name: file.name,
						size: file.size,
						path: file.path,
						type: file.type,
						key: file.key || newKey
					});

					if(!file.key) {
						file.key = newKey;
					}

					this.$emit('fileinput', {
						key: file.key || newKey,
						target: {files: [file]}
					});
					// this.localValue = this.localFiles
					// this.$emit('update:model-value', this.localValue);
				});
			}
		}
	},

	computed: {
		computedDisable() {
			if(this.field.multipleLimit) {
				return this.localFiles?.length >= this.field.multipleLimit
			}
		}
	}
};
</script>
